/**
 * Form ekranlarının ortak parçaları.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ÜÇ KURAL, ÜÇ FORMDA AYNI
 *
 * 1. DOĞRULAMA PAYLAŞILAN ŞEMADAN — sunucunun reddedeceği bir formu istemci
 *    kabul edemez [K-020]. Kural iki yerde yazılsaydı biri güncellenir,
 *    diğeri unutulurdu.
 * 2. DÜZENLEMEDE YALNIZ DEĞİŞEN ALAN GÖNDERİLİR — aşağıya bakın, bu bir
 *    veri kaybı korumasıdır.
 * 3. HATA METNİ EKRANDA YAZILMAZ — `api.ts` kod → Türkçe eşlemesini tek
 *    yerde tutuyor (C adımı).
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { ApiError, istek } from '../lib/api.ts';
import { Button } from '../design/Button.tsx';
import { Card, CardBody } from '../design/Card.tsx';
import { EmptyState, SkeletonList } from '../design/Feedback.tsx';
import { Select } from '../design/Field.tsx';
import { PageBody, PageHeader } from '../shell/AppShell.tsx';
import { YAZMA_SONRASI_ANAHTARLAR } from './api.ts';
import { izinVar, useSession } from '../auth/session.ts';

/* ── Değişen alanlar ─────────────────────────────────────────────────────── */

/**
 * PATCH gövdesine YALNIZ dokunulan alanları koyar.
 *
 * ─── NEDEN: SESSİZ ÜZERİNE YAZMA ────────────────────────────────────────────
 * Tüm formu göndermek daha kolaydı. Ama iki kişi aynı sahayı aynı dakikada
 * düzenlerse — sahada teknisyen telefonu açmış, ofiste operatör ekranı — ikinci
 * kaydeden, birincinin değiştirdiği alanı ESKİ değerine geri döndürür ve kimse
 * fark etmez. Sunucu bunu zaten destekliyor: "gönderilmeyen alana dokunulmaz"
 * (`sites.ts`, PATCH). Formun o desteği kullanması gerekiyor.
 *
 * `birlikte` grupları, tek başına anlamsız olan alan ikililerini taşır:
 * koordinat (yarım koordinat sahayı Afrika'nın ortasına taşır) ve tank
 * yerleşimi (`ck_site_tank`: saha varsa numara var).
 */
export function degisenAlanlar<T extends object>(
  degerler: T,
  dokunulan: Partial<Record<keyof T, unknown>>,
  birlikte: readonly (readonly (keyof T)[])[] = [],
): Partial<T> {
  const alanlar = new Set(
    (Object.keys(dokunulan) as (keyof T)[]).filter((k) => dokunulan[k] === true),
  );

  // Bir grubun herhangi bir üyesi değiştiyse GRUBUN TAMAMI gönderilir.
  for (const grup of birlikte) {
    if (grup.some((alan) => alanlar.has(alan))) {
      for (const alan of grup) alanlar.add(alan);
    }
  }

  const govde: Partial<T> = {};
  for (const alan of alanlar) govde[alan] = degerler[alan];
  return govde;
}

/* ── Kaydetme ────────────────────────────────────────────────────────────── */

export interface KaydetSonucu {
  readonly kaydet: (yol: string, method: 'POST' | 'PATCH', govde: unknown) => Promise<void>;
  readonly hata: ApiError | Error | null;
  readonly hataTemizle: () => void;
}

/**
 * Kaydeder, önbelleği tazeler, geri döner.
 *
 * Başarıdan sonra `donus` adresine gidiliyor: kullanıcı "kaydettim, şimdi ne
 * oldu?" diye bakakalmasın. Önbellek geçersiz kılınmazsa kullanıcı kendi
 * eklediği kaydı listede GÖREMEZ — formun en sık bildirilen hata sınıfı budur.
 */
export function useKaydet(donus: string): KaydetSonucu {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [hata, setHata] = useState<ApiError | Error | null>(null);

  return {
    hata,
    hataTemizle: () => setHata(null),
    kaydet: async (yol, method, govde) => {
      setHata(null);
      try {
        await istek(yol, { method, body: govde });
        for (const anahtar of YAZMA_SONRASI_ANAHTARLAR) {
          await qc.invalidateQueries({ queryKey: anahtar });
        }
        await navigate({ to: donus });
      } catch (error) {
        setHata(error instanceof Error ? error : new Error('Kaydedilemedi.'));
        // FIRLATMIYORUZ: hata ekranda gösteriliyor, form açık kalıyor ve
        // kullanıcının doldurduğu değerler duruyor.
      }
    },
  };
}

/**
 * Kaydetme hatası — `no_active_firm` HATA DEĞİL, eksik adımdır.
 *
 * C adımında `ApiError.firmaSecilmedi` bu ayrım için eklendi: kullanıcıya
 * "işlem başarısız" demek yerine NE YAPACAĞI söyleniyor ve oraya bir bağlantı
 * veriliyor. Kırmızı da göstermiyoruz — yaptığı bir yanlış yok.
 */
export function KaydetmeHatasi({
  hata,
}: {
  readonly hata: Error | null;
}): React.JSX.Element | null {
  if (hata === null) return null;

  if (hata instanceof ApiError && hata.firmaSecilmedi) {
    return (
      <div
        role="status"
        className="rounded-[var(--radius-card)] border border-border bg-surface p-3 text-sm text-ink-muted"
      >
        <p>{hata.message}</p>
        <Link to="/profil" className="mt-1 inline-block font-medium text-brand-700">
          Profil sayfasına git
        </Link>
      </div>
    );
  }

  return (
    <p role="alert" className="text-sm text-danger">
      {hata.message}
    </p>
  );
}

/* ── Form kabuğu ─────────────────────────────────────────────────────────── */

/**
 * Başlık + kart + kaydet/vazgeç — üç formda aynı iskelet.
 *
 * "Vazgeç" bir BAĞLANTI, düğme değil: tarayıcının geri/yeni sekmede aç
 * davranışları çalışsın. Kaydet düğmesi `loading` ile kendini kilitliyor —
 * çift gönderim mükerrer kayıt demektir (`Button` bunu içinde yapıyor).
 */
export function FormKabugu({
  baslik,
  aciklama,
  donus,
  gonderiliyor,
  kaydetEtiketi = 'Kaydet',
  onSubmit,
  children,
}: {
  readonly baslik: string;
  readonly aciklama?: string;
  readonly donus: string;
  readonly gonderiliyor: boolean;
  readonly kaydetEtiketi?: string;
  readonly onSubmit: (e: React.FormEvent) => void;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <>
      <PageHeader
        title={baslik}
        {...(aciklama === undefined ? {} : { description: aciklama })}
        action={
          <Link to={donus} className="text-sm font-medium text-brand-700">
            Vazgeç
          </Link>
        }
      />
      <PageBody>
        {/* `noValidate`: doğrulamayı zod yapıyor, tarayıcının kendi baloncukları
            Türkçe olmayabilir ve erişilebilirlik bağlarımızı bozar. */}
        <form onSubmit={onSubmit} noValidate>
          <Card>
            <CardBody>
              <div className="flex flex-col gap-4">{children}</div>
            </CardBody>
          </Card>

          <div className="mt-4 flex gap-3">
            <Button type="submit" size="lg" loading={gonderiliyor}>
              {kaydetEtiketi}
            </Button>
          </div>
        </form>
      </PageBody>
    </>
  );
}

/* ── İzin ve yükleme kapıları ────────────────────────────────────────────── */

/**
 * İzni olmayan kullanıcı forma ULAŞMAMALI — ama bu bir güvenlik kontrolü
 * DEĞİLDİR (asıl kapı sunucudaki `requirePermission`). Buradaki fayda şu:
 * yetkisiz kullanıcı formu doldurup kaydete bastıktan sonra 403 almasın.
 */
export function IzinKapisi({
  gerekli,
  children,
}: {
  readonly gerekli: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  const { data: session, isPending } = useSession();

  if (isPending) {
    return (
      <PageBody>
        <SkeletonList rows={2} />
      </PageBody>
    );
  }

  if (!izinVar(session, gerekli)) {
    return (
      <PageBody>
        <EmptyState
          title="Bu işlem için yetkiniz yok"
          description="Gerekli izni firma yöneticinizden isteyebilirsiniz."
        />
      </PageBody>
    );
  }

  return <>{children}</>;
}

/** Düzenleme formu, düzenlenecek kayıt gelene kadar ÇİZİLMEZ. */
export function KayitKapisi<T>({
  sorgu,
  bulunamadiBaslik,
  donus,
  children,
}: {
  readonly sorgu: { isPending: boolean; isError: boolean; error: unknown; data: T | undefined };
  readonly bulunamadiBaslik: string;
  readonly donus: string;
  readonly children: (kayit: T) => ReactNode;
}): React.JSX.Element {
  if (sorgu.isPending) {
    return (
      <PageBody>
        <SkeletonList rows={3} />
      </PageBody>
    );
  }

  if (sorgu.isError || sorgu.data === undefined) {
    // 404 ile geçici hata ayrımı — detay ekranındakiyle aynı gerekçe.
    const yok = sorgu.error instanceof ApiError && sorgu.error.status === 404;
    return (
      <PageBody>
        <EmptyState
          title={yok ? bulunamadiBaslik : 'Kayıt yüklenemedi'}
          description={
            yok
              ? 'Bu kayıt silinmiş olabilir ya da erişiminiz yok.'
              : sorgu.error instanceof Error
                ? sorgu.error.message
                : 'Bilinmeyen bir hata.'
          }
          action={
            <Link to={donus}>
              <Button variant="secondary">Listeye dön</Button>
            </Link>
          }
        />
      </PageBody>
    );
  }

  return <>{children(sorgu.data)}</>;
}

/* ── Seçim listeleri ─────────────────────────────────────────────────────── */

/**
 * SEÇENEKLER GELMEDEN SEÇİM ALANI ÇİZİLMEZ — ölçülmüş bir kusurun çözümü.
 *
 * ─── NE OLUYORDU ────────────────────────────────────────────────────────────
 * Seçenekler sunucudan asenkron geliyor. Form ilk çizildiğinde listede yalnız
 * "Seçin" var; form kütüphanesi kayıtlı değeri (örn. saha 7) DOM'a yazmaya
 * çalışıyor, o değere karşılık gelen `<option>` henüz olmadığı için tarayıcı
 * seçimi boşa düşürüyor. Seçenekler sonradan geldiğinde kimse değeri yeniden
 * uygulamıyor.
 *
 * Sonucu iki türlü ölçüldü: saha detayından "pano ekle" ile gelindiğinde ön
 * seçim kayboluyordu, ve DÜZENLEME formunda kayıtlı saha/profil/kapasite
 * "seçilmemiş" görünüyordu — yani ekran kaydın gerçek durumunu yanlış
 * gösteriyordu.
 *
 * Çözüm: seçenekler hazır olana kadar alan KAYITSIZ bir yer tutucu olarak
 * çiziliyor; hazır olunca gerçek alan takılıyor ve kayıtlı değer o anda
 * uygulanıyor. Yan kazanç: kullanıcı boş bir listeye bakıp "hiç saha yok mu?"
 * diye düşünmüyor.
 */
export function SecimYerTutucu({
  id,
  describedBy,
}: {
  readonly id: string;
  readonly describedBy: string | undefined;
}): React.JSX.Element {
  return (
    <Select id={id} aria-describedby={describedBy} disabled>
      <option>Yükleniyor…</option>
    </Select>
  );
}

/* ── Sayı alanları ───────────────────────────────────────────────────────── */

/**
 * `<input type="number">` DEĞERİ METİN OLARAK VERİR.
 *
 * Kaydedilmeden fark edilmeyen bir hata sınıfı: zod `z.number()` bekler,
 * metin gelir, form "Enlem sayı olmalı" der ve kullanıcı ekranda 38.5 yazdığı
 * hâlde neden reddedildiğini anlamaz. Dönüştürme kayıt anında yapılıyor.
 *
 * Boş alan: zorunlu alanlarda `undefined` (zod "gerekli" der), isteğe bağlı
 * alanlarda `null` (sunucuda "TEMİZLE" anlamına gelir — `isteğeBağlıMetin`
 * ile aynı ayrım).
 */
export const sayiAlani = {
  setValueAs: (v: unknown): number | undefined =>
    v === '' || v === null || v === undefined ? undefined : Number(v),
};

export const sayiAlaniBosalabilir = {
  setValueAs: (v: unknown): number | null =>
    v === '' || v === null || v === undefined ? null : Number(v),
};
